import type { IPriceProductIssue } from '~/modules/campaign-price/utils/priceProductStatus';
import type { ICampaignGroup } from '~/modules/campaign-group';
import type { Campaign } from '~/types/graphql';

// Straight at the file rather than the module root: the campaign-price barrel reaches back into
// campaign-template through a long chain of other modules, and a cycle would leave this unevaluated.
import { PRICE_PRODUCT_ISSUE_START_TOOLTIP } from '~/modules/campaign-price/constants/priceProductStatus';
import { useTemplateStatus } from './useTemplateStatus';
import { CAMPAIGN_TYPE } from '~/constants/campaign';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export interface IUseCampaignActionTooltipProps {
  campaign: Campaign | ICampaignGroup;
  isExistsVariantEmpty?: boolean;
  /** The Control/Variant product issue for a PRICE campaign — templates carry none of their own. */
  priceProductIssue?: IPriceProductIssue;
}
export const useCampaignActionTooltip = ({
  campaign,
  isExistsVariantEmpty,
  priceProductIssue,
}: IUseCampaignActionTooltipProps) => {
  const { t } = useTranslation();
  const { hasUnpublished, hasDeleted, hasUnpublishedAndDeleted, hasUnpublishedOrDeleted } =
    useTemplateStatus({
      campaign,
    });

  const startButtonTooltip = useMemo(() => {
    if (isExistsVariantEmpty) {
      if (campaign.type === CAMPAIGN_TYPE.PRICE) return t('Set up Variant price to start');
      if (campaign.type === CAMPAIGN_TYPE.URL) return t('Select URLs to start');

      return t('Select templates to start');
    }

    if (priceProductIssue) return t(PRICE_PRODUCT_ISSUE_START_TOOLTIP[priceProductIssue]);

    if (hasUnpublishedAndDeleted) {
      return t('Publish & replace templates to start');
    }

    if (hasUnpublished) {
      return t('Publish all templates to start');
    }

    if (hasDeleted) {
      return t('Replace deleted templates to start');
    }

    return undefined;
  }, [
    campaign.type,
    hasDeleted,
    hasUnpublished,
    hasUnpublishedAndDeleted,
    isExistsVariantEmpty,
    priceProductIssue,
    t,
  ]);

  return {
    startButtonTooltip,
    hasUnpublishedOrDeleted: hasUnpublishedOrDeleted || !!priceProductIssue,
  };
};
